import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Grid from '@material-ui/core/Grid';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import CreateFormResolver from 'features/shared/utility/createFormResolver';
import addPersonSchema from 'features/user/schema/addPersonSchema';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import {
  DEFAULT_VALUES,
  CONTACT_METHOD_TYPE,
  NAME_SNAPSHOT_FIELDS,
} from '../../constants/addPersonFormConstants';
import useUserDictionaryOptions from 'features/user/hooks/useUserDictionaryOptions';
import {
  RMSTextField,
  RMSSelect,
  RMSDatePicker,
  RMSCheckbox,
} from 'features/shared/components/form-controls';
import AddIcon from '@material-ui/icons/Add';
import { SectionCard } from 'features/shared/components/cards';
import { RMSIconButton } from 'features/shared/components/buttons';
import InfoIcon from '@material-ui/icons/Info';
import SearchAddressAutoComplete from 'features/shared/components/modules/SearchAddressAutoComplete';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import CityManagementDialog from '../common/CityManagementDialog';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import { usePersonNameDialog } from 'features/user/hooks/usePersonNameDialog';
import { createPerson, editPerson, getPersonDetails } from 'features/user/services/userService';
import { buildPersonPayload, buildEditPersonPayload } from 'features/user/utility/addPersonHelper';
import { notify } from 'reducers/NotifierReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';

const PersonDetailsForm = forwardRef(
  ({ open = true, personId = null, onSaved, onLoadingChange, onStateChange }, ref) => {
    const cityDictPerms = useSelector((state) => state.permissions?.dictionary?.codeCities);

    const isEditMode = Boolean(personId);
    const personPerms = usePermissions(PERMISSION_RESOURCES.PERSON);
    const canSavePerson = isEditMode ? personPerms.Edit : personPerms.Create;
    const isViewOnly = isEditMode && !personPerms.Edit;
    const showCityAddIcon = Boolean(
      cityDictPerms?.Create || cityDictPerms?.Edit || cityDictPerms?.Delete
    );

    const methods = useForm({
      resolver: CreateFormResolver(addPersonSchema),
      mode: 'onSubmit',
      defaultValues: DEFAULT_VALUES,
      disabled: isViewOnly,
    });

    const { control, setValue } = methods;
    const { isDirty } = useFormState({ control });

    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const dispatch = useDispatch();
    const { openAddressDialog } = useAddressDialog();
    const nameSnapshotRef = useRef(null);

    const isDeceased = useWatch({ control, name: 'Deceased' });
    const isDangerous = useWatch({ control, name: 'Dangerous' });
    const ptsAddressID = useWatch({ control, name: 'ptsAddressID' });
    const contactMethodType = useWatch({ control, name: 'ContactMethodType' });
    const [recordCreated, recordCreatedBy, recordUpdated, recordUpdatedBy] = useWatch({
      control,
      name: ['Created', 'CreatedBy', 'Updated', 'UpdatedBy'],
    });

    const {
      prefixOptions,
      suffixOptions,
      raceOptions,
      sexOptions,
      cityOptions,
      stateOptions,
      countyOptions,
      countryOptions,
      deathCauseOptions,
      residentOptions,
      citizenshipOptions,
      nationalityOptions,
      gangOptions,
      maritalStatusOptions,
      ethnicityOptions,
      eyeColorOptions,
      handedOptions,
      bloodTypeOptions,
      hairColorOptions,
      hairLengthOptions,
      hairStyleOptions,
      contactTypeOptions,
      languageOptions,
      dangerousTypeOptions,
    } = useUserDictionaryOptions();

    const birthCityOptions = useMemo(
      () =>
        cityOptions.map((city) => ({
          ...city,
          Description: city.State
            ? `${city.CityDescription} - ${city.State}`
            : city.CityDescription,
        })),
      [cityOptions]
    );

    const contactMethodTypeOptions = useMemo(
      () => [
        { Code: CONTACT_METHOD_TYPE.PHONE, Description: CONTACT_METHOD_TYPE.PHONE },
        { Code: CONTACT_METHOD_TYPE.EMAIL, Description: CONTACT_METHOD_TYPE.EMAIL },
      ],
      []
    );

    const [cityManagementDialogOpen, setCityManagementDialogOpen] = useState(false);

    const { openPersonNameDialog } = usePersonNameDialog();
    const [submitting, setSubmitting] = useState(false);
    const [loadingPerson, setLoadingPerson] = useState(false);

    const handleAddressSelection = useCallback(
      (address) => {
        setValue('ptsAddressID', address?.ptsAddressID ?? null, { shouldDirty: true });
        setValue('AddressSearch', address?.ptsAddressID ?? null, { shouldDirty: true });
      },
      [setValue]
    );

    const handleBirthCityCreated = useCallback(
      (city) => {
        setValue('ptsBirthCityID', city?.ptsCityID ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      [setValue]
    );

    const hasNameChanged = useCallback((data) => {
      if (!nameSnapshotRef.current) return false;
      return NAME_SNAPSHOT_FIELDS.some(
        (field) => (data[field] || '') !== nameSnapshotRef.current[field]
      );
    }, []);

    const performSave = useCallback(
      async (data) => {
        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: async () => {
            let savedPersonId = personId;

            if (isEditMode) {
              await editPerson(personId, buildEditPersonPayload(data));
            } else {
              const created = await createPerson(buildPersonPayload(data));
              savedPersonId = created.ptsPersonID;
            }

            const [personDetails] = await getPersonDetails(savedPersonId);
            return personDetails || { ptsPersonID: savedPersonId };
          },
          onSuccess: (person) => {
            dispatch(
              notifyDataUpdate({
                type: 'Person',
                data: {
                  action: isEditMode ? 'Update' : 'Create',
                  ptsPersonID: person?.ptsPersonID,
                },
              })
            );
            dispatch(
              notify(
                isEditMode ? 'Person updated successfully' : 'Person created successfully',
                'success'
              )
            );

            if (person) {
              methods.reset({ ...DEFAULT_VALUES, ...person });
              nameSnapshotRef.current = NAME_SNAPSHOT_FIELDS.reduce(
                (snapshot, field) => ({ ...snapshot, [field]: person[field] || '' }),
                {}
              );
            }

            onSaved?.(person, { isEditMode });
          },
        });
      },
      [personId, isEditMode, dispatch, methods, onSaved]
    );

    const onSubmit = useCallback(
      async (data) => {
        if (isEditMode && hasNameChanged(data)) {
          openPersonNameDialog({
            personId,
            nameChangeSnapshot: nameSnapshotRef.current,
            onSaved: () => performSave(data),
          });
          return;
        }

        await performSave(data);
      },
      [isEditMode, hasNameChanged, performSave, openPersonNameDialog, personId]
    );

    const handleSave = useCallback((event) => methods.handleSubmit(onSubmit)(event), [
      methods,
      onSubmit,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        submit: handleSave,
        resetForm: () => methods.reset(DEFAULT_VALUES),
        isDirty: () => isDirtyRef.current,
      }),
      [handleSave, methods]
    );

    useEffect(() => {
      onLoadingChange?.(submitting || loadingPerson);
    }, [submitting, loadingPerson, onLoadingChange]);

    useEffect(() => {
      if (!open || !personId) return;

      executeApiAction({
        setLoading: setLoadingPerson,
        dispatch,
        action: () => getPersonDetails(personId),
        onSuccess: ([person]) => {
          if (person) {
            methods.reset({ ...DEFAULT_VALUES, ...person });
            nameSnapshotRef.current = NAME_SNAPSHOT_FIELDS.reduce(
              (snapshot, field) => ({ ...snapshot, [field]: person[field] || '' }),
              {}
            );
          }
        },
      });
    }, [open, personId, dispatch, methods]);

    useEffect(() => {
      onStateChange?.({
        isDirty,
        canSave: canSavePerson,
        submitting,
        isEditMode,
        recordCreated,
        recordCreatedBy,
        recordUpdated,
        recordUpdatedBy,
      });
    }, [
      onStateChange,
      isDirty,
      canSavePerson,
      submitting,
      isEditMode,
      recordCreated,
      recordCreatedBy,
      recordUpdated,
      recordUpdatedBy,
    ]);

    return (
      <>
        <FormProvider {...methods}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="pd-column">
                {/* Name section */}
                <SectionCard label="Name">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4} md={3} lg={3}>
                      <RMSSelect
                        name="Prefix"
                        label="Prefix"
                        options={prefixOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={3}>
                      <RMSTextField name="FirstName" label="First Name" />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={3}>
                      <RMSTextField name="MiddleName" label="Middle Name" />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={3}>
                      <RMSTextField name="LastName" label="Last Name" />
                    </Grid>
                    <Grid item lg={12}>
                      <RMSSelect
                        name="Suffix"
                        label="Suffix"
                        options={suffixOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item lg={12}>
                      <RMSTextField name="NickName" label="Nick Name" />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* age section */}
                <SectionCard label="Age">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <RMSDatePicker name="Born" label="Birth Date" maxDate={new Date()} />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="ptsBirthCityID"
                        label="Birth City"
                        options={birthCityOptions}
                        {...getDictionarySelectProps()}
                        showLabelOnly
                        clearable
                        prefixIcon={showCityAddIcon ? <AddIcon fontSize="small" /> : undefined}
                        onPrefixIconClick={
                          showCityAddIcon ? () => setCityManagementDialogOpen(true) : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="BirthState"
                        label="Birth State"
                        options={stateOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSSelect
                        name="BirthCounty"
                        label="Birth County"
                        options={countyOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSSelect
                        name="BirthCountry"
                        label="Birth Country"
                        options={countryOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2}>
                      <RMSCheckbox name="Deceased" label="Deceased" />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSDatePicker name="Died" label="Deceased Date" disabled={!isDeceased} />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="CauseOfDeath"
                        label="Cause of Death"
                        options={deathCauseOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* Advanced Details section */}
                <SectionCard label="Advanced Details">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="Gang"
                        label="Gang"
                        options={gangOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="MaritalStatus"
                        label="Marital Status"
                        options={maritalStatusOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSTextField name="EducationLevel" label="Education Level" />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSTextField name="Allergies" label="Allergies" />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSTextField name="DNA" label="DNA" />
                    </Grid>
                    <Grid item xs={12}>
                      <RMSTextField name="Notes" label="Person Notes" multiline rows={3} />
                    </Grid>
                  </Grid>
                </SectionCard>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className="pd-column">
                {/* General Details section */}
                <SectionCard label="General Details">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <RMSTextField name="GeneralAppearance" label="General Appearance" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSSelect
                        name="Race"
                        label="Race"
                        options={raceOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSSelect
                        name="Sex"
                        label="Sex"
                        options={sexOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSTextField
                        name="Height"
                        label="Height"
                        type="number"
                        inputProps={{ maxLength: 3 }}
                        rightContent={
                          <RMSIconButton
                            icon={<InfoIcon />}
                            tooltip="Enter feet then inches as one number. E.g. 501 = 5ft 1in, 611 = 6ft 11in"
                            color="primary"
                            size="small"
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSTextField
                        name="Weight"
                        label="Weight"
                        type="number"
                        inputProps={{ maxLength: 3 }}
                      />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* Citizenship section */}
                <SectionCard label="Citizenship">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="LocalResident"
                        label="Local Resident"
                        options={residentOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSCheckbox name="IsUSCitizen" label="US Citizen" />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="CurrentCitizenship"
                        label="Current Citizenship"
                        options={citizenshipOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSSelect
                        name="BirthCitizenship"
                        label="Birth Citizenship"
                        options={citizenshipOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RMSSelect
                        name="BirthNationality"
                        label="Birth Nationality"
                        options={nationalityOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* Features section */}
                <SectionCard label="Features">
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="Ethnicity"
                        label="Ethnicity"
                        options={ethnicityOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="EyeColor"
                        label="Eye Color"
                        options={eyeColorOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="Handed"
                        label="Handed"
                        options={handedOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="BloodType"
                        label="Blood Type"
                        options={bloodTypeOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="HairColor"
                        label="Hair Color"
                        options={hairColorOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="HairLength"
                        label="Hair Length"
                        options={hairLengthOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RMSSelect
                        name="HairStyle"
                        label="Hair Style"
                        options={hairStyleOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                  </Grid>
                </SectionCard>

                {/* Credentials section */}
                {!isEditMode && (
                  <SectionCard label="Credentials">
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <RMSTextField name="SSN" label="SSN" />
                      </Grid>
                      <Grid item xs={4}>
                        <RMSTextField name="OLN" label="OLN" />
                      </Grid>
                      <Grid item xs={4}>
                        <RMSSelect
                          name="OLNState"
                          label="State"
                          options={stateOptions}
                          {...getDictionarySelectProps()}
                        />
                      </Grid>
                    </Grid>
                  </SectionCard>
                )}

                {/* Contact section */}
                {!isEditMode && (
                  <SectionCard label="Contact">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SearchAddressAutoComplete
                          name="AddressSearch"
                          label="Address Lookup"
                          defaultAddressId={ptsAddressID}
                          onSelectionChange={handleAddressSelection}
                          onAddIconClick={() =>
                            openAddressDialog(null, {
                              closeOnSave: true,
                              onAddressCreated: handleAddressSelection,
                            })
                          }
                          showDetailsIcon={false}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <RMSCheckbox name="IsPrimaryAddress" label="Primary" />
                      </Grid>
                      <Grid item xs={3}>
                        <RMSSelect
                          name="ContactMethodType"
                          label="Method Type"
                          options={contactMethodTypeOptions}
                          {...getDictionarySelectProps()}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <RMSSelect
                          name="ContactType"
                          label="Contact Type"
                          options={contactTypeOptions}
                          disabled={!contactMethodType}
                          {...getDictionarySelectProps()}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <RMSTextField
                          name="ContactInfo"
                          label="Info"
                          disabled={!contactMethodType}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <RMSCheckbox name="IsDefaultContact" label="Default" />
                      </Grid>
                      <Grid item xs={10}>
                        <RMSTextField name="ContactNotes" label="Notes" />
                      </Grid>
                    </Grid>
                  </SectionCard>
                )}
              </div>
            </Grid>

            <Grid item xs={12}>
              <div className="pd-column">
                {/* Flags section */}
                <SectionCard label="Flags Details">
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <RMSCheckbox name="PrintsAvailable" label="Finger Print Available" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="OverrideJuvenile" label="Override Juvenile" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="PublicOfficial" label="Public Official" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="EnglishLangIndicator" label="English Speaking" />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="PrimaryLanguage"
                        label="Primary Language"
                        options={languageOptions}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="HasDigitalImage" label="Digital Image" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Incarcerated" label="In Jail" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="ConcealedWeaponPermit" label="Carry Weapon" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Dangerous" label="Dangerous" />
                    </Grid>
                    <Grid item xs={4}>
                      <RMSSelect
                        name="WhyDangerous"
                        label="Why Dangerous"
                        options={dangerousTypeOptions}
                        disabled={!isDangerous}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="IsOrganDonor" label="Organ Donor" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="ExFelon" label="Ex Felon" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="ProtectiveOrder" label="Protective Order" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="SeniorCitizen" label="Senior Citizen" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Officer" label="Officer" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="SexOffender" label="Sex Offender" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Juvenile" label="Juvenile" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Military" label="Military" />
                    </Grid>
                    <Grid item xs={2}>
                      <RMSCheckbox name="Probation" label="Probation" />
                    </Grid>
                  </Grid>
                </SectionCard>
              </div>
            </Grid>
          </Grid>
        </FormProvider>

        {cityManagementDialogOpen && (
          <CityManagementDialog
            open={cityManagementDialogOpen}
            onClose={() => setCityManagementDialogOpen(false)}
            onCityCreated={handleBirthCityCreated}
          />
        )}
      </>
    );
  }
);

export default PersonDetailsForm;
